import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Plane, MapPin } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { RESERVA_STATUS_CLASS, RESERVA_STATUS_LABELS } from "@/pages/trips/shared";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Lunes como primer día de la rejilla (6 semanas)
function buildGrid(anchor) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7; // 0=Lun
  const start = new Date(first);
  start.setDate(first.getDate() - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export default function TripsCalendar({ onSelectTrip }) {
  const [anchor, setAnchor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const grid = useMemo(() => buildGrid(anchor), [anchor]);
  const from = ymd(grid[0]);
  const to = ymd(grid[41]);
  const todayStr = ymd(new Date());

  const { data, isLoading } = useQuery({
    queryKey: ["trips-calendar", from, to],
    queryFn: async () => (await api.get("/trips/calendar", { params: { from, to } })).data,
    keepPreviousData: true,
  });

  const byDate = useMemo(() => {
    const map = {};
    for (const e of data?.events || []) { (map[e.date] = map[e.date] || []).push(e); }
    return map;
  }, [data]);

  const move = (delta) => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1));

  return (
    <div className="space-y-3" data-testid="trips-calendar">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-bold">{MONTHS[anchor.getMonth()]} {anchor.getFullYear()}</h2>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setAnchor(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); })} data-testid="cal-today">Hoy</Button>
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-2">
          {isLoading ? (
            <Skeleton className="h-[480px] w-full" />
          ) : (
            <>
              <div className="grid grid-cols-7 border-b">
                {WEEKDAYS.map((w) => <div key={w} className="p-2 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{w}</div>)}
              </div>
              <div className="grid grid-cols-7">
                {grid.map((d, i) => {
                  const key = ymd(d);
                  const inMonth = d.getMonth() === anchor.getMonth();
                  const isToday = key === todayStr;
                  const events = byDate[key] || [];
                  return (
                    <div key={i} className={`min-h-[84px] border-b border-r p-1 ${inMonth ? "" : "bg-muted/30"}`} data-testid={`cal-day-${key}`}>
                      <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs ${isToday ? "bg-primary font-bold text-primary-foreground" : inMonth ? "text-foreground" : "text-muted-foreground"}`}>{d.getDate()}</div>
                      <div className="space-y-0.5">
                        {events.slice(0, 3).map((e, j) => (
                          <button
                            key={j}
                            onClick={() => onSelectTrip(e.trip_id)}
                            title={`${e.title} · ${e.subtitle || ""}`}
                            data-testid={`cal-event-${e.trip_id}`}
                            className={`flex w-full items-center gap-1 truncate rounded px-1 py-0.5 text-left text-[10px] hover:opacity-80 ${e.kind === "trip" ? "bg-primary/15 text-primary" : RESERVA_STATUS_CLASS[e.status] || "bg-muted"}`}
                          >
                            {e.kind === "trip" ? <Plane className="h-2.5 w-2.5 shrink-0" /> : <MapPin className="h-2.5 w-2.5 shrink-0" />}
                            <span className="truncate">{e.title}</span>
                          </button>
                        ))}
                        {events.length > 3 && <div className="px-1 text-[10px] text-muted-foreground">+{events.length - 3} más</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-primary/15" /> Inicio de viaje</span>
        {Object.entries(RESERVA_STATUS_LABELS).filter(([k]) => k !== "cancelada").map(([k, v]) => (
          <span key={k} className="flex items-center gap-1"><span className={`inline-block h-3 w-3 rounded ${RESERVA_STATUS_CLASS[k]}`} /> {v}</span>
        ))}
      </div>
    </div>
  );
}
